import { useQuery } from '@tanstack/react-query';
import { loadImporterReportPreview } from '../utils/api';
import { resolveSeasonLivePack } from '../utils/seasonFromActive';

/**
 * Live Season summary for one scope x commodity that has no pre-generated
 * pack. Single-cell preview call: the backend aggregates on the fly and answers
 * `live: true` with the same `preview` shape as the bundle packs, or
 * `empty: true` (`preview: null`) when the scope has no volume. Query key
 * includes the account so ACL-gated packs never leak across users.
 *
 * status: 'idle' | 'loading' | 'ready' | 'empty' | 'error' (for the CURRENT scope).
 */
export default function useSeasonLivePack({
  scope = '',
  commodity = '',
  email = '',
  enabled = false,
} = {}) {
  const active = Boolean(enabled && scope && commodity);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['importer-report', 'season-live', email || '', scope, commodity],
    queryFn: ({ signal }) =>
      loadImporterReportPreview({
        destination: scope,
        commodity,
        kind: 'season_to_date',
        signal,
      }).catch((err) => {
        // 404 = no Season summary for this destination: same state as "no volume".
        if (err?.status === 404) return { status: true, live: true, empty: true, preview: null };
        throw err;
      }),
    enabled: active,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return resolveSeasonLivePack({ active, isLoading, isError, data });
}
